'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Card } from '@morpho-org/ui-v6/server';
import { Icon } from '@repo/ui';
import { Alert } from '@repo/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useState } from 'react';
import type { MarketLendWithdrawSource } from '@/lib/analytics/client/events';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { TransferError } from '@/components/common/Error/TransferErrors';
import { MarketLendSummaryCard } from '@/components/common/MarketLendSummary';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import MarketLendWithdrawReviewDialogV2 from '@/components/MarketPage/MarketActionV2/MarketLendWithdrawReviewDialogV2';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getMarketGeoRestrictionActionProps } from '@/helpers/marketGeoRestriction';
import useMarketTooltip from '@/hooks/operation/market/useMarketTooltip';
import { useMarketLendWithdrawForm } from '@/hooks/operation/market/v2/useMarketLendWithdrawForm';
import usePositionManagerMarketDialogLabels from '@/hooks/operation/position-manager/usePositionManagerMarketDialogLabels';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import { useMarketGeoRestriction } from '@/hooks/useMarketGeoRestriction';
import { useTrackMarketActionsV2EntryViewed } from '@/lib/analytics/client/hooks/useTrackMarketActionsV2EntryViewed';
import { trackMarketActionsV2EntryClicked } from '@/lib/analytics/client/trackers/market-actions-v2';
import { buildMarketLendWithdrawCompletedParams, trackMarketLendWithdrawCompleted } from '@/lib/analytics/client/trackers/market-lend';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import PositionManagerDialogLoading from '../PositionManagerDialogLoading';
import { useLendWithdrawDialogData } from '../useMarketDialogData';
interface PositionManagerLendWithdrawDialogContentProps {
  marketId: MarketId;
  chainId: number;
  source: MarketLendWithdrawSource;
}

/**
 * Position Manager withdrawal flow for a direct market lend position, with its market
 * query, sticky MAX input, summary, review, simulation, and transaction wiring.
 */
function PositionManagerLendWithdrawDialogContent(t0) {
  const $ = _c(96);
  const {
    marketId,
    chainId,
    source
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const queryClient = useQueryClient();
  const {
    toggleDialog
  } = useDialogContext();
  let t1;
  if ($[0] !== chainId || $[1] !== marketId) {
    t1 = {
      chainId,
      marketId
    };
    $[0] = chainId;
    $[1] = marketId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: dialogData,
    isPending: queryIsLoading,
    error: queryError
  } = useLendWithdrawDialogData(t1);
  dialogData?.loanAsset;
  const t2 = dialogData?.loanAsset;
  let t3;
  if ($[3] !== t2) {
    t3 = parseApiAsset(t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const loanAsset = t3;
  let t4;
  if ($[5] !== dialogData?.collateralAsset) {
    t4 = () => parseApiAsset(dialogData?.collateralAsset);
    $[5] = dialogData?.collateralAsset;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  dialogData?.collateralAsset;
  let t5;
  if ($[7] !== t4) {
    t5 = t4();
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const collateralAsset = t5;
  const marketParams = dialogData?.marketParams;
  let t6;
  if ($[9] !== loanAsset || $[10] !== marketParams) {
    t6 = {
      marketParams,
      loanAsset
    };
    $[9] = loanAsset;
    $[10] = marketParams;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    isMarketLendEnabled,
    inputWithdrawAmount,
    setInputWithdrawAmountManually,
    positionData,
    positionError,
    isPositionLoading,
    isReallocationLoading,
    currentSupply,
    afterSupply,
    maxWithdrawAssets,
    isMaxActive,
    onMaxClick,
    shouldUseSupplyShares,
    reallocations,
    showLiquidityWarning,
    buildErrorMessage,
    canTransferLoanAsset,
    isFinalizeDisabled,
    isButtonLoading,
    isDebouncing,
    reset
  } = useMarketLendWithdrawForm(t6);
  const [isInputPending, setIsInputPending] = useState(false);
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.blueBundlesV1", loanAsset?.address, targetChain.id);
  const t7 = dialogData?.collateralAsset?.address;
  const t8 = dialogData?.loanAsset.address;
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = [t7, t8];
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== chainId || $[16] !== t9) {
    t10 = {
      chainId,
      assetAddresses: t9
    };
    $[15] = chainId;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const geoRestriction = useMarketGeoRestriction(t10);
  let t11;
  if ($[18] !== isMarketLendEnabled || $[19] !== marketId || $[20] !== source || $[21] !== targetChain.id || $[22] !== targetChain.name) {
    t11 = {
      entryPoint: "position_manager",
      action: "withdraw",
      marketId,
      chainId: targetChain.id,
      chainName: targetChain.name,
      source,
      enabled: isMarketLendEnabled
    };
    $[18] = isMarketLendEnabled;
    $[19] = marketId;
    $[20] = source;
    $[21] = targetChain.id;
    $[22] = targetChain.name;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useTrackMarketActionsV2EntryViewed(t11);
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [];
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== buildErrorMessage || $[26] !== inputWithdrawAmount) {
    t13 = {
      operationLabel: "withdraw",
      inputCollateralAmount: ZERO,
      inputLoanAmount: inputWithdrawAmount,
      isNew: true,
      localErrorMessage: buildErrorMessage,
      operations: t12,
      simulationError: undefined,
      blockError: null,
      isPending: false
    };
    $[25] = buildErrorMessage;
    $[26] = inputWithdrawAmount;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const {
    confirmButtonLabel
  } = usePositionManagerMarketDialogLabels(t13);
  const tooltipContent = useMarketTooltip(buildErrorMessage);
  const t14 = isFinalizeDisabled || !bundleCanTransfer;
  let t15;
  if ($[28] !== confirmButtonLabel || $[29] !== geoRestriction || $[30] !== isButtonLoading || $[31] !== t14 || $[32] !== tooltipContent) {
    t15 = getMarketGeoRestrictionActionProps({
      geoRestriction,
      isFinalizeDisabled: t14,
      isLoading: isButtonLoading,
      finalizeLabel: confirmButtonLabel,
      tooltipContent
    });
    $[28] = confirmButtonLabel;
    $[29] = geoRestriction;
    $[30] = isButtonLoading;
    $[31] = t14;
    $[32] = tooltipContent;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let geoActionProps;
  let shouldBlockFinalize;
  if ($[34] !== t15) {
    ({
      shouldBlockFinalize,
      ...geoActionProps
    } = t15);
    $[34] = t15;
    $[35] = geoActionProps;
    $[36] = shouldBlockFinalize;
  } else {
    geoActionProps = $[35];
    shouldBlockFinalize = $[36];
  }
  let t16;
  if ($[37] !== account.isContract || $[38] !== account.isSafe || $[39] !== collateralAsset || $[40] !== dialogData?.avgApy || $[41] !== dialogData?.marketForRewards || $[42] !== inputWithdrawAmount || $[43] !== loanAsset || $[44] !== marketId || $[45] !== marketParams || $[46] !== queryClient || $[47] !== reallocations || $[48] !== reset || $[49] !== shouldBlockFinalize || $[50] !== shouldUseSupplyShares || $[51] !== source || $[52] !== targetChain.id || $[53] !== targetChain.name || $[54] !== toggleDialog) {
    t16 = () => {
      if (!marketParams || !loanAsset || shouldBlockFinalize) {
        return;
      }
      trackMarketActionsV2EntryClicked({
        entryPoint: "position_manager",
        action: "withdraw",
        marketId,
        chainId: targetChain.id,
        chainName: targetChain.name,
        source
      });
      toggleDialog(<MarketLendWithdrawReviewDialogV2 marketParams={marketParams} amount={inputWithdrawAmount} shouldUseSupplyShares={shouldUseSupplyShares} reallocations={reallocations} collateralAsset={collateralAsset} loanAsset={loanAsset} marketForRewards={dialogData?.marketForRewards} avgSupplyApy={dialogData?.avgApy} onTxSuccess={withdrawAmount => {
        const params = buildMarketLendWithdrawCompletedParams({
          marketId,
          loanAsset,
          withdrawAmount,
          isFullExit: shouldUseSupplyShares,
          chainId: targetChain.id,
          chainName: targetChain.name,
          walletType: getWalletType(account.isSafe, account.isContract),
          entryPoint: "position_manager",
          source
        });
        if (params) {
          trackMarketLendWithdrawCompleted(params);
        }
        queryClient.invalidateQueries();
        reset();
      }} entryPoint="position_manager" />, POSITION_MANAGER_DIALOG_OPTIONS);
    };
    $[37] = account.isContract;
    $[38] = account.isSafe;
    $[39] = collateralAsset;
    $[40] = dialogData?.avgApy;
    $[41] = dialogData?.marketForRewards;
    $[42] = inputWithdrawAmount;
    $[43] = loanAsset;
    $[44] = marketId;
    $[45] = marketParams;
    $[46] = queryClient;
    $[47] = reallocations;
    $[48] = reset;
    $[49] = shouldBlockFinalize;
    $[50] = shouldUseSupplyShares;
    $[51] = source;
    $[52] = targetChain.id;
    $[53] = targetChain.name;
    $[54] = toggleDialog;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const handleFinalize = t16;
  if (queryIsLoading) {
    let t17;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <PositionManagerDialogLoading />;
      $[56] = t17;
    } else {
      t17 = $[56];
    }
    return t17;
  }
  if (queryError || positionError && !positionData || !loanAsset) {
    let t17;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <ErrorServiceUnavailable minHeight="360px" />;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    return t17;
  }
  const t17 = `Withdraw ${loanAsset.symbol}`;
  const t18 = isMarketLendEnabled && account.isConnected && (isPositionLoading || isReallocationLoading);
  let t19;
  if ($[58] !== inputWithdrawAmount || $[59] !== isMaxActive || $[60] !== loanAsset || $[61] !== maxWithdrawAssets || $[62] !== onMaxClick || $[63] !== setInputWithdrawAmountManually || $[64] !== t17 || $[65] !== t18) {
    t19 = <CompactAssetInput header={t17} amount={inputWithdrawAmount} token={loanAsset} onChange={setInputWithdrawAmountManually} maxValue={maxWithdrawAssets} onMaxClick={onMaxClick} onPendingChange={setIsInputPending} isMax={isMaxActive} loading={t18} autoFocus={true} />;
    $[58] = inputWithdrawAmount;
    $[59] = isMaxActive;
    $[60] = loanAsset;
    $[61] = maxWithdrawAssets;
    $[62] = onMaxClick;
    $[63] = setInputWithdrawAmountManually;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== showLiquidityWarning) {
    t20 = showLiquidityWarning && <Card className="gap-2xs mt-xs bg-warning flex-row items-start shadow-none" padding="s" role="status" aria-live="polite" data-testid="withdraw-liquidity-warning"><Icon className="text-warning-foreground" icon={<Alert />} aria-hidden="true" /><span className="text-2xs text-warning-foreground">Available liquidity limits the amount you can withdraw.</span></Card>;
    $[67] = showLiquidityWarning;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== canTransferLoanAsset || $[70] !== loanAsset.address) {
    t21 = !canTransferLoanAsset && <TransferError tokenAddress={loanAsset.address} />;
    $[69] = canTransferLoanAsset;
    $[70] = loanAsset.address;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t19 || $[73] !== t20 || $[74] !== t21) {
    t22 = <div className="flex flex-col gap-[2px]">{t19}{t20}{t21}</div>;
    $[72] = t19;
    $[73] = t20;
    $[74] = t21;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  const t23 = dialogData?.marketForRewards;
  const t24 = dialogData?.avgApy;
  let t25;
  if ($[76] !== afterSupply || $[77] !== currentSupply || $[78] !== isPositionLoading || $[79] !== loanAsset || $[80] !== marketId || $[81] !== t23 || $[82] !== t24) {
    t25 = <MarketLendSummaryCard marketId={marketId} currentSupply={currentSupply} afterSupply={afterSupply} loanAsset={loanAsset} isLoading={isPositionLoading} marketForRewards={t23} avgSupplyApy={t24} background="muted" />;
    $[76] = afterSupply;
    $[77] = currentSupply;
    $[78] = isPositionLoading;
    $[79] = loanAsset;
    $[80] = marketId;
    $[81] = t23;
    $[82] = t24;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  let t26;
  if ($[84] !== bundleCanTransfer) {
    t26 = !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={MARKET_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[84] = bundleCanTransfer;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== geoActionProps || $[87] !== handleFinalize || $[88] !== isDebouncing || $[89] !== isInputPending) {
    t27 = <ActionButtons handleFinalize={handleFinalize} {...geoActionProps} isInputPending={isInputPending} isValidating={isDebouncing} />;
    $[86] = geoActionProps;
    $[87] = handleFinalize;
    $[88] = isDebouncing;
    $[89] = isInputPending;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  let t28;
  if ($[91] !== t22 || $[92] !== t25 || $[93] !== t26 || $[94] !== t27) {
    t28 = <>{t22}{t25}{t26}{t27}</>;
    $[91] = t22;
    $[92] = t25;
    $[93] = t26;
    $[94] = t27;
    $[95] = t28;
  } else {
    t28 = $[95];
  }
  return t28;
}
export default memo(PositionManagerLendWithdrawDialogContent);